<template>
  <div
    v-if="dossier"
    class="squircle-t-lg sticky top-0 right-0 left-0 z-50 flex flex-row items-center gap-x-3 rounded-t bg-linear-to-r p-3 transition-all duration-250"
    :class="{
      'via-primary-600 from-red-500 from-10% via-60% to-orange-400 to-90% dark:from-red-900 dark:to-orange-800':
        hasSpecialParticularities && hasInfection,
      'to-primary-600 from-red-500 from-10%':
        !hasSpecialParticularities && hasInfection,
      'from-primary dark:from-primary-900 via-primary-700 via-60% to-orange-400 to-90% dark:to-orange-800':
        hasSpecialParticularities && !hasInfection,
      'from-primary to-primary-700 dark:from-primary-900 dark:to-primary-700':
        !dossier.light_form && !hasSpecialParticularities && !hasInfection,
      'from-gray-400 to-gray-600': dossier.light_form,
      'h-32': !isCropped,
      'h-18': isCropped
    }"
  >
    <div
      v-if="hasInfection"
      class="absolute inset-0 overflow-hidden text-white transition-all duration-200"
      :class="{
        'text-9xl': !isCropped,
        'text-5xl': isCropped
      }"
    >
      <FontAwesomeIcon
        :icon="['fas', 'virus-covid']"
        class="-translate-x-1/3 -translate-y-1/3 opacity-25"
        size="3x"
      />
    </div>
    <!-- The image content -->
    <div
      class="flex max-w-fit flex-1 flex-row items-end gap-x-2"
      :class="{
        'absolute top-3 left-3': requirePictureOutside
      }"
    >
      <div
        class="squircle-md group/dossier-picture transition-width relative overflow-hidden rounded-md border border-slate-300 bg-white p-0.5 shadow-xl"
        :class="{
          'aspect-photo w-32': requirePictureOutside,
          'aspect-photo w-20': isNotCropped,
          'aspect-square w-12': isCropped
        }"
      >
        <img
          v-if="dossier?.pictures?.picture_rectangle?.original_url"
          :src="
            cropped
              ? dossier.pictures?.picture_square?.original_url
              : dossier.pictures?.picture_rectangle?.original_url
          "
          class="squircle-md w-full overflow-hidden rounded object-cover"
          :alt="dossier.concernedPerson.full_name"
        />
        <div
          v-else
          class="text-primary-200 absolute inset-0 flex flex-row items-center justify-center"
        >
          <FontAwesomeLayers
            class="transition-all"
            :class="{
              'fa-5x': requirePictureOutside,
              'fa-4x': isNotCropped,
              'fa-2x': isCropped
            }"
          >
            <FontAwesomeIcon :icon="['fal', 'circle']" />
            <FontAwesomeIcon :icon="['fas', 'camera']" transform="shrink-7" />
          </FontAwesomeLayers>
        </div>
        <div
          v-if="canEditPicture"
          class="absolute right-1 bottom-1 flex cursor-pointer flex-row items-center justify-center"
          :class="{}"
        >
          <Popover class="inline-flex">
            <Float portal :offset="10">
              <PopoverButton
                class="bg-primary-100/75 rounded p-0.5 text-gray-600"
                @click="fetchAllImagesFromGallery"
              >
                <FontAwesomeIcon :icon="['fal', 'edit']" fixed-width />
              </PopoverButton>

              <PopoverPanel
                v-slot="{ close }"
                class="popover-panel overflow-hidden"
              >
                <div
                  ref="selectionPicture"
                  class="flex max-h-110 max-w-150 flex-col items-start justify-start gap-5 overflow-x-hidden p-5"
                  :class="{
                    'pointer-events-none overflow-y-hidden': pictureWaiting,
                    'overflow-y-auto': !pictureWaiting
                  }"
                >
                  <div
                    v-if="dossierPicture"
                    class="flex w-full flex-col items-start justify-start"
                  >
                    <div class="relative h-30 w-31.25 overflow-hidden rounded">
                      <img
                        :alt="dossierPicture.name"
                        :src="dossierPicture.original_url"
                        class="h-full w-full object-cover"
                      />
                      <div
                        class="absolute inset-0 z-1 flex flex-col items-end justify-between bg-black/35 p-1"
                      >
                        <div
                          v-if="mainPictureGalleryId"
                          class="flex flex-row gap-1"
                        >
                          <button
                            class="flex items-center justify-center rounded bg-white p-1.5"
                            @click="
                              $router.push({
                                name: 'dossier_gallery_edit_route',
                                params: {
                                  id: dossier.id,
                                  pictureId: mainPictureGalleryId
                                }
                              })
                            "
                          >
                            <FontAwesomeIcon
                              :icon="['fal', 'edit']"
                              fixed-width
                            />
                          </button>
                          <button
                            class="flex items-center justify-center rounded bg-linear-to-br from-emerald-400 to-black p-1.5 text-white"
                            @click="
                              $router.push({
                                name: 'dossier_gallery_ai_route',
                                params: {
                                  id: dossier.id,
                                  pictureId: mainPictureGalleryId
                                }
                              })
                            "
                          >
                            <FontAwesomeIcon
                              :icon="['fal', 'microchip-ai']"
                              fixed-width
                            />
                          </button>
                        </div>
                        <button
                          class="self-start"
                          @click="
                            $router.push({
                              name: 'dossier_picture_preview_route'
                            })
                          "
                        >
                          <FontAwesomeIcon
                            :icon="['fal', 'eye']"
                            class="text-white"
                            fixed-width
                          />
                        </button>
                      </div>
                    </div>
                  </div>

                  <div
                    class="flex w-full min-w-32 flex-row flex-wrap items-center justify-start gap-5"
                  >
                    <label
                      :title="$t('button.add')"
                      class="flex h-30 w-31.25 cursor-pointer items-center justify-center overflow-hidden rounded border border-gray-300 hover:bg-gray-100"
                    >
                      <FontAwesomeIcon :icon="['fas', 'plus']" fixed-width />

                      <input
                        ref="profilInput"
                        type="file"
                        class="hidden"
                        @change="saveAndSetAsMainPicture($event, close)"
                      />
                    </label>
                    <button
                      v-for="(picture, idx) in pictureImages"
                      :key="idx"
                      :title="picture.name"
                      class="relative h-30 w-31.25 overflow-hidden rounded"
                      @click="setAsMainPicture(picture, close)"
                    >
                      <img
                        :alt="picture.name"
                        :src="picture.original_url"
                        class="h-full w-full object-cover"
                      />

                      <span
                        class="absolute inset-0 z-1 bg-black/35 opacity-0 transition-all hover:opacity-100"
                      ></span>
                    </button>
                  </div>
                </div>

                <div
                  v-if="pictureWaiting"
                  class="absolute inset-0 z-2 flex items-center justify-center rounded bg-black/5"
                >
                  <div
                    class="border-primary-500 h-10 w-10 animate-spin rounded-full border-4 border-l-transparent"
                  ></div>
                </div>
              </PopoverPanel>
            </Float>
          </Popover>
        </div>
      </div>

      <slot name="header_actions" />
    </div>

    <!-- The content text -->
    <div
      class="flex min-w-0 flex-1 flex-row text-white"
      :class="{
        'pl-36': requirePictureOutside
      }"
    >
      <div class="flex min-w-0 flex-1 flex-col gap-2">
        <div class="flex flex-col">
          <div
            class="flex flex-wrap"
            :class="{
              'text-2xl': isCropped,
              'text-3xl': isNotCropped || requirePictureOutside
            }"
          >
            <span
              v-if="!isCropped"
              class="capitalize-first mr-2 leading-none whitespace-nowrap"
            >
              {{ civiliteFormated }}
            </span>
            <span
              class="truncate leading-none font-bold text-ellipsis whitespace-nowrap"
            >
              {{ dossier.concernedPerson.full_name }}
            </span>
            <span
              class="hidden"
              data-testid="dossier_concerned_person_last_name"
            >
              {{ dossier.concernedPerson.last_name }}
            </span>
            <span
              class="hidden"
              data-testid="dossier_concerned_person_first_name"
            >
              {{ dossier.concernedPerson.first_name }}
            </span>
          </div>
          <div
            v-if="!isCropped"
            class="inline-block overflow-hidden leading-none text-ellipsis whitespace-nowrap"
          >
            {{ lifeSpan }}&nbsp;
          </div>
        </div>
        <div
          v-if="
            !isCropped && Array.isArray(timelineEvents) && timelineEvents.length
          "
          class="hidden @4xl:flex"
        >
          <div
            class="flex flex-row items-center gap-0.5 rounded border text-xs font-bold [&>*:first-child]:rounded-l [&>*:last-child]:rounded-r [&>svg:last-child]:hidden"
          >
            <template v-for="event in timelineEvents" :key="event.id">
              <span class="px-2">
                {{ event.name }}
              </span>
              <svg
                class="h-5 text-gray-300 dark:text-slate-700"
                viewBox="0 0 22 80"
                fill="none"
                preserveAspectRatio="none"
              >
                <path
                  d="M0 -2L20 40L0 82"
                  vector-effect="non-scaling-stroke"
                  stroke="currentcolor"
                  stroke-linejoin="round"
                />
              </svg>
            </template>
          </div>
        </div>
      </div>
      <div
        v-if="!isCropped"
        class="hidden shrink-0 flex-col items-end gap-1.5 @4xl/dossier-main:flex"
      >
        <div
          class="capitalize-first text-2xl leading-none font-bold whitespace-nowrap"
        >
          {{ funeralHomeName }}
        </div>
        <div class="flex flex-row justify-end">
          <div>
            <AvatarComponent
              :user_id="dossier.responsable_id"
              :badge="true"
              size="6"
            />
          </div>
        </div>
        <div v-if="!!dossier.properties" class="flex flex-row gap-x-1">
          <div
            v-if="!!dossier.properties.destitute"
            class="text-contrast-primary capitalize-first rounded-md bg-rose-600 px-2 py-0.5 text-xs font-medium whitespace-nowrap"
          >
            {{ $t('attributes.destitute') }}
          </div>
          <div
            v-if="!!dossier.properties.funeral_in_strict_privacy"
            class="text-contrast-primary capitalize-first rounded-md bg-rose-600 px-2 py-0.5 text-xs font-medium whitespace-nowrap"
          >
            {{ $t('attributes.funeral_in_strict_privacy') }}
          </div>
          <div
            v-if="!!dossier.properties.no_flower"
            class="text-contrast-primary capitalize-first rounded-md bg-rose-600 px-2 py-0.5 text-xs font-medium whitespace-nowrap"
          >
            {{ $t('attributes.no_flower') }}
          </div>
        </div>
      </div>
      <div
        v-if="!isCropped && !!dossier.properties?.funeral_in_strict_privacy"
        class="flex shrink-0 items-center @4xl/dossier-main:hidden"
      >
        <div
          class="text-contrast-primary capitalize-first rounded-md bg-rose-600 px-2 py-0.5 text-xs font-medium whitespace-nowrap"
        >
          {{ $t('attributes.funeral_in_strict_privacy') }}
        </div>
      </div>
      <div
        class="flex shrink-0 items-start gap-1.5"
        :class="{
          'flex-col @2xl/dossier-main:flex-row @4xl/dossier-main:hidden':
            !isCropped,
          'flex-row': isCropped
        }"
      >
        <FuneralHomeAvatar :id="dossier.funeral_home_id" />
        <AvatarComponent :user_id="dossier.responsable_id" />
      </div>
    </div>
  </div>
</template>

<script>
import AvatarComponent from '@c/AvatarComponent.vue'
import {
  FontAwesomeIcon,
  FontAwesomeLayers
} from '@fortawesome/vue-fontawesome'
import { mapActions, mapGetters } from 'vuex'
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue'
import { Float } from '@headlessui-float/vue'
import { EventType } from '@/assets/enums/index.js'
import eventType from '@/assets/enums/events/EventType.js'
import FuneralHomeAvatar from '@c/Avatar/FuneralHomeAvatar.vue'

export default {
  name: 'DossierInfosHeader',
  components: {
    FuneralHomeAvatar,
    PopoverPanel,
    PopoverButton,
    Float,
    Popover,
    AvatarComponent,
    FontAwesomeLayers,
    FontAwesomeIcon
  },
  props: {
    containerScrollName: {
      type: String,
      required: false,
      default: null
    },
    pictureOutside: {
      type: Boolean,
      required: false,
      default: false
    },
    pictureEditable: {
      type: Boolean,
      required: false,
      default: true
    }
  },
  data() {
    return {
      cropped: false,
      pictureWaiting: false,
      containerScroll: null,
      dataPictureImages: []
    }
  },
  computed: {
    ...mapGetters({
      dossier: 'dossier/getDossier',
      civilities: 'metadata/getCivilityMetadata',
      getFuneralHome: 'metadata/getFuneralHomeMetadata'
    }),
    canEditPicture() {
      return !this.cropped && this.pictureEditable
    },
    isCropped() {
      return this.cropped && !this.pictureOutside
    },
    isNotCropped() {
      return !this.cropped && !this.pictureOutside
    },
    requirePictureOutside() {
      return this.pictureOutside
    },
    hasInfection() {
      return !!this.dossier?.properties?.infection
    },
    hasSpecialParticularities() {
      return (
        !!this.dossier?.properties?.no_flower ||
        !!this.dossier?.properties?.funeral_in_strict_privacy ||
        !!this.dossier?.properties?.destitute
      )
    },
    civiliteFormated() {
      return (
        this.civilities.find(
          (civility) => civility.id === this.dossier.concernedPerson.civility
        )?.name ?? null
      )
    },
    funeralHomeName() {
      return this.getFuneralHome(this.dossier.funeral_home_id)?.name
    },
    dossierPicture() {
      return this.dossier.pictures?.picture
    },
    mainPictureGalleryId() {
      const fromMedia = this.dossierPicture?.custom_properties?.from_media
      if (!fromMedia) {
        return null
      }

      return (
        this.dataPictureImages.find((img) => img.id === fromMedia)?.id ?? null
      )
    },
    pictureImages() {
      if (this.dossierPicture) {
        return this.dataPictureImages.filter(
          (img) => img.id !== this.dossierPicture.custom_properties.from_media
        )
      }

      return this.dataPictureImages
    },
    lifeSpan() {
      const locale = this.$i18n.locale.substring(0, 2)
      const birth = this.$h.formatDate(
        this.dossier.concernedPerson.birth_date,
        'L',
        locale
      )
      const { death_date_time, age } = this.dossier.concernedPerson

      if (!death_date_time) return birth

      const death = this.$h.formatDate(death_date_time, 'L', locale)
      return `${birth} - ${death} (${age})`
    },
    timelineEvents() {
      return [
        {
          id: 'LDEC',
          name:
            this.dossier.concernedPerson.place_of_death?.full_name ??
            this.dossier.concernedPerson.place_of_death?.name?.[
              this.$i18n.locale
            ] ??
            this.dossier.concernedPerson.place_of_death?.name ??
            this.$t('attributes.death_place')
        },
        ...(this.dossier.events ?? [])
          .filter(
            (event) =>
              event.type === EventType.TRANSFERT && event.destination_kind
          )
          .map((event) => {
            return {
              name:
                event.destination?.model?.full_name ??
                event.destination?.model?.name ??
                this.$t(`enums.event.type.${eventType.TRANSFERT}`)
            }
          })
      ]
    }
  },
  mounted() {
    this.checkScrollEvent()
  },
  methods: {
    ...mapActions({
      processFetchDossierGalleryAction: 'dossier/fetchDossierGallery',
      processUsePictureGalleryForPictureDossierAction:
        'dossier/usePictureGalleryForPictureDossier',
      processFetchDossierByIdAction: 'dossier/fetchDossierByID',
      processPostPicturesToDossierGalleryAction:
        'dossier/postPicturesToDossierGallery'
    }),
    saveAndSetAsMainPicture(event, closeCallback = null) {
      if (!this.pictureWaiting) {
        this.pictureWaiting = true
      }

      const file = event.target.files[0]

      if (file) {
        const formData = new FormData()

        formData.append('pictures[]', file)

        this.processPostPicturesToDossierGalleryAction({
          dossier_id: this.dossier.id,
          form_data: formData
        }).then((res) => {
          const image = res.pictures_gallery.at(-1)

          this.setAsMainPicture(image, closeCallback)

          event.target.files = new DataTransfer().files
        })
      }
    },
    setAsMainPicture(picture, closeCallback = null) {
      if (!this.pictureWaiting) {
        this.pictureWaiting = true
      }

      this.processUsePictureGalleryForPictureDossierAction({
        media_id: picture.id,
        dossier_id: this.dossier.id
      }).then((res) => {
        this.pictureWaiting = false

        this.processFetchDossierByIdAction(this.dossier.id)
        this.fetchAllImagesFromGallery()
      })
    },
    fetchAllImagesFromGallery() {
      const id = this.$route.params?.id

      this.processFetchDossierGalleryAction({
        dossier_id: id,
        params: {
          media_type: 'image'
        }
      }).then((res) => {
        this.dataPictureImages = res.pictures_gallery
      })
    },
    checkScrollEvent() {
      if (this.containerScrollName) {
        this.containerScroll = document.getElementById(this.containerScrollName)

        if (this.containerScroll) {
          this.containerScroll.addEventListener('scroll', (e) => {
            const scrollTop = this.containerScroll.scrollTop

            if (scrollTop >= 75) {
              if (!this.cropped) {
                this.cropped = true
              }
            }

            if (scrollTop === 0) {
              if (this.cropped) {
                this.cropped = false
              }
            }
          })
        }
      }
    }
  }
}
</script>
